import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useMemberAuth } from '../../../../contexts/MemberAuthContext';
import { mfetch, MemberApiError, PlanRuleRow } from '../../api';
import Icon, { IconName } from '../../design/Icon';
import { Toggle } from '../../design/atoms';

/* SPEC 12/13 — automations. One trigger, one action, always readable as a
   single sentence; if it can't be, it doesn't ship. */

interface Choice {
  key: string;
  icon: IconName;
  label: string;
  /** Lower-case clause used to build the preview sentence. */
  clause: string;
}

const TRIGGERS: Choice[] = [
  { key: 'booking_created', icon: 'cal', label: 'I book a class or PT', clause: 'I book a class or PT session' },
  { key: 'workout_unlogged', icon: 'clock', label: 'A workout goes unlogged', clause: 'a planned workout is not logged by 10 pm' },
  { key: 'schedule', icon: 'bell', label: "It's a set day & time", clause: "it's Sunday at 8 pm" },
  { key: 'checkin', icon: 'qr', label: 'I check in at the gym', clause: 'I check in at the gym' },
  { key: 'target_hit_streak', icon: 'up', label: 'I hit a target 3× in a row', clause: 'I hit a target 3 sessions in a row' },
  { key: 'class_cancelled', icon: 'x', label: 'The studio cancels a class', clause: 'the studio cancels a booked class' },
];

const ACTIONS: Choice[] = [
  { key: 'roll_forward', icon: 'right', label: 'Move it to my next free day', clause: 'move the workout to my next free day' },
  { key: 'mark_covered', icon: 'check', label: 'Mark the day as covered', clause: "mark that day's conditioning as covered" },
  { key: 'raise_target', icon: 'up', label: 'Raise the target 5%', clause: 'raise the target 5% on the next occurrence' },
  { key: 'notify', icon: 'bell', label: 'Nudge me on WhatsApp', clause: 'send me a nudge' },
  { key: 'open_plan', icon: 'book', label: "Open today's plan", clause: "open today's plan on my next app open" },
  { key: 'request_review', icon: 'user', label: 'Ask my trainer to review', clause: 'ask my trainer to review my plan' },
];

const byKey = (list: Choice[], key: string) => list.find(c => c.key === key);

/** The one sentence a rule must always be expressible as. */
export function ruleSentence(triggerKey: string, actionKey: string): string {
  const t = byKey(TRIGGERS, triggerKey);
  const a = byKey(ACTIONS, actionKey);
  if (!t || !a) return 'Pick a trigger and an action.';
  return `When ${t.clause}, ${a.clause}.`;
}

export default function MemberPlanRulesPage() {
  const { token } = useMemberAuth();
  const navigate = useNavigate();

  const [rules, setRules] = useState<PlanRuleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [triggerKey, setTriggerKey] = useState(TRIGGERS[0].key);
  const [actionKey, setActionKey] = useState(ACTIONS[0].key);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    mfetch(token, '/member/plan/rules')
      .then(r => setRules(r.data ?? []))
      .catch(e => setError(e instanceof MemberApiError ? e.message : 'Could not load your automations'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(load, [load]);

  const toggleRule = async (rule: PlanRuleRow, enabled: boolean) => {
    if (!token) return;
    // Optimistic — a toggle that lags feels broken.
    setRules(prev => prev.map(r => (r.id === rule.id ? { ...r, enabled } : r)));
    try {
      await mfetch(token, `/member/plan/rules/${rule.id}`, { method: 'PATCH', body: { enabled } });
    } catch (e) {
      setRules(prev => prev.map(r => (r.id === rule.id ? { ...r, enabled: rule.enabled } : r)));
      toast.error(e instanceof MemberApiError ? e.message : 'Could not update that rule');
    }
  };

  const deleteRule = async (rule: PlanRuleRow) => {
    if (!token) return;
    const previous = rules;
    setRules(prev => prev.filter(r => r.id !== rule.id));
    try {
      await mfetch(token, `/member/plan/rules/${rule.id}`, { method: 'DELETE' });
      toast.success('Rule removed');
    } catch (e) {
      setRules(previous);
      toast.error(e instanceof MemberApiError ? e.message : 'Could not remove that rule');
    }
  };

  const saveRule = async () => {
    if (!token || saving) return;
    setSaving(true);
    try {
      const r = await mfetch(token, '/member/plan/rules', { method: 'POST', body: { triggerKey, actionKey } });
      setRules(prev => [...prev, r.data as PlanRuleRow]);
      setComposing(false);
      toast.success('Rule saved');
    } catch (e) {
      toast.error(e instanceof MemberApiError ? e.message : 'Could not save that rule');
    } finally {
      setSaving(false);
    }
  };

  const pickRow = (c: Choice, on: boolean, onPick: () => void) => (
    <button
      key={c.key}
      className="lrow"
      onClick={onPick}
      style={{
        padding: '10px 12px',
        borderRadius: 'var(--r-s)',
        border: `1px solid ${on ? 'var(--ink)' : 'transparent'}`,
        background: on ? 'var(--tint2)' : 'transparent',
      }}
    >
      <div className="li gold"><Icon n={c.icon} s={16} c="var(--gold-2)" /></div>
      <div className="col" style={{ flex: 1, minWidth: 0 }}>
        <div className="mid trunc">{c.label}</div>
      </div>
      {on && <Icon n="check" s={17} c="var(--gold-2)" />}
    </button>
  );

  return (
    <div className="mpage">
      <div className="row" style={{ paddingTop: 8, gap: 10 }}>
        <button className="icon-btn" aria-label="Back" onClick={() => navigate(-1)}>
          <Icon n="left" s={18} c="var(--ink-2)" />
        </button>
        <div className="col" style={{ gap: 1, flex: 1, minWidth: 0 }}>
          <div className="dp dp-m">Automations</div>
          <div className="tiny">Small rules that keep the plan honest</div>
        </div>
        {!composing && (
          <button className="btn gold sm" onClick={() => setComposing(true)}>＋ New</button>
        )}
      </div>

      {/* Composer (SPEC 13) */}
      {composing && (
        <>
          <div className="card ink" style={{ padding: 15 }}>
            <div className="col" style={{ gap: 6 }}>
              <div className="lbl">Preview</div>
              <div className="sub wrap" style={{ color: 'var(--on-ink)', fontSize: 14.5 }}>
                {ruleSentence(triggerKey, actionKey)}
              </div>
            </div>
          </div>

          <div className="col" style={{ gap: 6 }}>
            <div className="lbl" style={{ paddingLeft: 2 }}>When</div>
            <div className="card" style={{ padding: 6 }}>
              {TRIGGERS.map(c => pickRow(c, c.key === triggerKey, () => setTriggerKey(c.key)))}
            </div>
          </div>

          <div className="col" style={{ gap: 6 }}>
            <div className="lbl-d" style={{ paddingLeft: 2 }}>Then</div>
            <div className="card" style={{ padding: 6 }}>
              {ACTIONS.map(c => pickRow(c, c.key === actionKey, () => setActionKey(c.key)))}
            </div>
          </div>

          <button className="btn gold block" disabled={saving} onClick={saveRule}>
            {saving ? 'Saving…' : 'Save rule'}
          </button>
          <button className="btn ghost block" onClick={() => setComposing(false)}>Cancel</button>
        </>
      )}

      {/* Existing rules */}
      {!composing && (
        loading ? (
          <div className="col" style={{ gap: 9 }}>
            {[0, 1, 2].map(i => <div key={i} className="skel" style={{ height: 96 }} />)}
          </div>
        ) : error ? (
          <div className="card flat">
            <div className="sub wrap">{error}</div>
            <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={load}>Retry</button>
          </div>
        ) : rules.length === 0 ? (
          <div className="card dashed" style={{ textAlign: 'center', padding: 24 }}>
            <div className="mid">No automations yet</div>
            <div className="sub wrap" style={{ marginTop: 6 }}>
              One trigger, one action — like “When a planned workout is not logged by 10 pm, move the workout to my next free day.”
            </div>
            <button className="btn gold sm" style={{ marginTop: 14 }} onClick={() => setComposing(true)}>Create a rule</button>
          </div>
        ) : (
          rules.map(rule => {
            const t = byKey(TRIGGERS, rule.triggerKey);
            const a = byKey(ACTIONS, rule.actionKey);
            return (
              <div key={rule.id} className="card" style={{ padding: 15 }}>
                <div className="between">
                  <div className="row" style={{ gap: 10, minWidth: 0 }}>
                    <div className="li gold"><Icon n={t?.icon ?? 'spark'} s={16} c="var(--gold-2)" /></div>
                    <div className="col" style={{ gap: 1, minWidth: 0 }}>
                      <div className="lbl">{rule.enabled ? 'Active' : 'Paused'}</div>
                      {rule.lastFiredAt && (
                        <div className="tiny trunc">
                          Last fired {new Date(rule.lastFiredAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </div>
                      )}
                    </div>
                  </div>
                  <Toggle on={rule.enabled} onChange={v => toggleRule(rule, v)} />
                </div>

                <div className="divider" style={{ margin: '13px 0 11px' }} />

                <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
                  <div className="lbl" style={{ width: 34, flex: '0 0 34px' }}>When</div>
                  <div className="sub wrap">{t?.clause ?? rule.triggerKey}</div>
                </div>
                <div style={{ width: 1, height: 10, background: 'var(--line)', margin: '4px 0 4px 17px' }} />
                <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
                  <div className="lbl-d" style={{ width: 34, flex: '0 0 34px' }}>Then</div>
                  <div className="sub wrap">{a?.clause ?? rule.actionKey}</div>
                </div>

                <button
                  className="btn ghost danger sm"
                  style={{ marginTop: 13 }}
                  onClick={() => deleteRule(rule)}
                >
                  Remove
                </button>
              </div>
            );
          })
        )
      )}
    </div>
  );
}
